<template>
  <div class="tutor-recommendations">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="mb-0">
        <i class="fas fa-star me-2"></i>
        Tutor Raccomandati per {{ subjectName }}
      </h4>
      <button v-if="showBackButton" class="btn btn-outline-secondary btn-sm" @click="$emit('back')">
        <i class="fas fa-arrow-left me-1"></i>
        Indietro
      </button>
    </div>

    <!-- Emergency request indicator -->
    <div v-if="hasEmergencyRequest" class="alert alert-warning mb-3">
      <i class="fas fa-bolt me-2"></i>
      <strong>Richiesta urgente attiva!</strong> I tutor con disponibilità immediata sono evidenziati.
    </div>

    <!-- Loading state -->
    <div v-if="loading && recommendations.length === 0" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Caricamento raccomandazioni...</span>
      </div>
      <p class="mt-3">Cerchiamo i migliori tutor per te...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="fas fa-exclamation-circle me-2"></i>
      {{ error }}
    </div>

    <!-- No recommendations -->
    <div v-else-if="!loading && recommendations.length === 0" class="alert alert-info">
      <i class="fas fa-info-circle me-2"></i>
      Non ci sono tutor disponibili per questa materia al momento.
    </div>

    <!-- Recommendations list -->
    <div v-else>
      <div v-for="rec in displayedRecommendations" :key="rec.tutorId">
        <TutorRecommendationCard
          :recommendation="rec"
          :time-slots="tutorTimeSlots[rec.tutorId] || []"
          :is-loading-slots="loadingSlots[rec.tutorId] || false"
          @view-details="handleViewDetails"
          @view-profile="handleViewProfile"
          @book-slot="handleBookSlot"
        />
      </div>

      <!-- Load More button -->
      <div v-if="hasMore" class="text-center mt-3">
        <button
          class="btn btn-outline-primary"
          @click="loadMore"
          :disabled="loading"
        >
          <span v-if="loading">
            <span class="spinner-border spinner-border-sm me-2"></span>
            Caricamento...
          </span>
          <span v-else>
            <i class="fas fa-chevron-down me-2"></i>
            Carica altri tutor
          </span>
        </button>
      </div>

      <!-- Show all tutors link -->
      <div class="text-center mt-4">
        <button class="btn btn-link" @click="$emit('show-all-tutors')">
          Vedi tutti i tutor per {{ subjectName }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import TutorRecommendationCard from './TutorRecommendationCard.vue';
import { getRecommendations } from '@/api/matchmaking';
import axios from 'axios';
import config from '@/utils/config';
import moment from 'moment';

export default {
  name: 'TutorRecommendations',
  components: {
    TutorRecommendationCard
  },
  props: {
    studentId: {
      type: Number,
      required: true
    },
    subjectName: {
      type: String,
      required: true
    },
    showBackButton: {
      type: Boolean,
      default: true
    },
    initialCount: {
      type: Number,
      default: 5
    }
  },
  data() {
    return {
      recommendations: [],
      displayCount: this.initialCount,
      loading: false,
      error: null,
      hasEmergencyRequest: false,
      tutorTimeSlots: {},
      loadingSlots: {}
    };
  },
  computed: {
    displayedRecommendations() {
      return this.recommendations.slice(0, this.displayCount);
    },
    hasMore() {
      return this.displayCount < this.recommendations.length;
    }
  },
  mounted() {
    this.loadRecommendations();
  },
  methods: {
    async loadRecommendations() {
      this.loading = true;
      this.error = null;

      try {
        const response = await getRecommendations(
          this.studentId,
          15, // Fetch more than initial to allow "Load More"
          [this.subjectName]
        );

        this.recommendations = response.recommendations || [];
        this.hasEmergencyRequest = response.lastMinuteRequestActive || false;

        // Load time slots for initially displayed tutors
        this.displayedRecommendations.forEach(rec => {
          this.loadTutorTimeSlots(rec.tutorId);
        });

      } catch (error) {
        console.error('Error loading recommendations:', error);
        this.error = 'Impossibile caricare le raccomandazioni. Riprova più tardi.';
      } finally {
        this.loading = false;
      }
    },
    async loadTutorTimeSlots(tutorId) {
      if (this.tutorTimeSlots[tutorId]) {
        return; // Already loaded
      }

      this.$set(this.loadingSlots, tutorId, true);

      try {
        // Get tutor's availability for this subject
        const response = await axios.get(
          `${config.baseUrl}/availability/insegnanti/${this.subjectName}`
        );

        // Filter for this specific tutor and future slots
        const tutorAvailability = response.data.filter(
          avail => avail.utente.id === tutorId
        );

        // Convert to time slots (similar to DisponibilitaMateria logic)
        const slots = await this.convertToTimeSlots(tutorAvailability);

        // Sort by date and take first 5
        const sortedSlots = slots.sort((a, b) => {
          const dateA = new Date(`${a.data}T${a.oraInizio}`);
          const dateB = new Date(`${b.data}T${b.oraInizio}`);
          return dateA - dateB;
        });

        this.$set(this.tutorTimeSlots, tutorId, sortedSlots.slice(0, 5));

      } catch (error) {
        console.error(`Error loading time slots for tutor ${tutorId}:`, error);
        this.$set(this.tutorTimeSlots, tutorId, []);
      } finally {
        this.$set(this.loadingSlots, tutorId, false);
      }
    },
    async convertToTimeSlots(disponibilitaList) {
      const slots = [];
      const now = new Date();

      disponibilitaList.forEach(disponibilita => {
        let startTime = moment(disponibilita.oraInizio, 'HH:mm');
        let endTime = moment(disponibilita.oraFine, 'HH:mm');

        // Check if this availability is in the future
        const availDate = new Date(`${disponibilita.dataInizio}T${disponibilita.oraInizio}`);
        if (availDate <= now) {
          return; // Skip past slots
        }

        while (startTime < endTime) {
          let nextTime = moment.min(startTime.clone().add(1, 'hour'), endTime);
          slots.push({
            disponibilitaId: disponibilita.id,
            data: disponibilita.dataInizio,
            oraInizio: startTime.format('HH:mm'),
            oraFine: nextTime.format('HH:mm'),
            giornoSettimana: disponibilita.giornoSettimana,
            utente: disponibilita.utente,
            maxPartecipantiGruppo: disponibilita.maxPartecipantiGruppo
          });
          startTime = nextTime;
        }
      });

      // Check which slots are already booked
      const availableSlots = [];
      for (const slot of slots) {
        try {
          const localDateTimeString = `${slot.data}T${slot.oraInizio}`;
          const response = await axios.get(
            `${config.baseUrl}/ripetizione/prenotazioni/${localDateTimeString}/${slot.disponibilitaId}/${slot.utente.id}`
          );
          if (!response.data) {
            availableSlots.push(slot);
          }
        } catch (error) {
          console.error('Error checking slot availability:', error);
        }
      }

      return availableSlots;
    },
    loadMore() {
      const newCount = this.displayCount + 5;
      this.displayCount = Math.min(newCount, this.recommendations.length);

      // Load time slots for newly displayed tutors
      const newlyDisplayed = this.recommendations.slice(
        this.displayCount - 5,
        this.displayCount
      );
      newlyDisplayed.forEach(rec => {
        this.loadTutorTimeSlots(rec.tutorId);
      });
    },
    handleViewDetails(recommendation) {
      this.$emit('view-tutor-details', {
        tutorId: recommendation.tutorId,
        tutorName: recommendation.tutorName,
        subject: this.subjectName
      });
    },
    handleViewProfile(tutorId) {
      this.$emit('view-profile', tutorId);
    },
    handleBookSlot({ tutor, slot }) {
      this.$emit('book-slot', {
        tutor,
        slot,
        subject: this.subjectName
      });
    }
  }
};
</script>

<style scoped>
.tutor-recommendations {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.alert {
  border-radius: 0.5rem;
}
</style>
